import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { loadCarnival, loginCarnival, syncReport } from './carnivalSlice';
import './carnival.css';

const isTrue = (v) => String(v || '').toLowerCase() === 'true';

/**
 * ExtraMilePlay logo + Back button, top-left on every screen (back("...") in the PHP pages). Inside the
 * mobile app the external Back button is hidden and the logo asks the app to navigate back instead.
 * `backTo` makes Back a link inside the carnival (e.g. game -> hub) instead of leaving for ExtraMilePlay.
 */
function CarnivalHeader({ backTo, children }) {
  const session = useSelector((s) => s.carnival.session);
  const logo = session?.companyLogoUrl || process.env.REACT_APP_EM_LOGO;
  const home = process.env.REACT_APP_BASE_URL;
  const backUrl = session?.backButtonRedirect || home;
  const fromApp = isTrue(session?.fromMobileApp);

  const onLogo = (e) => {
    if (!fromApp) return;
    e.preventDefault();
    window.GameStatus?.postMessage?.('navigate_back');
  };

  return (
    <header className="cc-header">
      <a href={home} onClick={onLogo}>
        {logo && <img src={logo} className="cc-logo" alt="Extramile Play" />}
      </a>
      <div className="cc-header-actions">
        {backTo ? (
          <Link to={backTo} className="cc-btn">
            Back
          </Link>
        ) : (
          !fromApp && (
            <a href={backUrl} className="cc-btn">
              Back
            </a>
          )
        )}
      </div>
      {children && <div className="cc-header-extra">{children}</div>}
    </header>
  );
}

/**
 * Full-screen page frame: background art (desktop / phone variants), the header, and the session guard —
 * logs the player in, loads (creating on first visit) their progress, and makes sure their report exists.
 */
export default function CarnivalShell({ bg, bgMob, bgColor, backTo, headerExtra, className = '', children }) {
  const dispatch = useDispatch();
  const { session, game, authStatus, authError, loadStatus, loadError, reportId, reportTried } = useSelector((s) => s.carnival);

  useEffect(() => {
    if (!session && authStatus === 'idle') dispatch(loginCarnival());
  }, [session, authStatus, dispatch]);

  useEffect(() => {
    if (session && loadStatus === 'idle') dispatch(loadCarnival());
  }, [session, loadStatus, dispatch]);

  // A player with no report yet (first visit, or an earlier report call failed) gets one created now.
  useEffect(() => {
    if (session && game && !reportId && !reportTried) dispatch(syncReport({ points: game.points, time: game.time }));
  }, [session, game, reportId, reportTried, dispatch]);

  const style = {};
  if (bg) style['--cc-bg'] = `url(${bg})`;
  if (bgColor) style['--cc-bg-color'] = bgColor;
  if (bgMob) style['--cc-bg-mob'] = `url(${bgMob})`;

  if (!session || !game) {
    const failed = (!session && authStatus === 'failed') || (session && loadStatus === 'failed');
    const message = !session ? authError : loadError;
    return (
      <div className={`cc-page cc-message ${className}`} style={style}>
        {failed ? (
          <>
            <p>{message || 'Something went wrong.'}</p>
            {session ? (
              <button type="button" className="cc-btn" onClick={() => dispatch(loadCarnival())}>
                Try again
              </button>
            ) : (
              <a className="cc-btn" href={process.env.REACT_APP_BASE_URL}>
                Go to ExtraMilePlay
              </a>
            )}
          </>
        ) : (
          <p>Loading…</p>
        )}
      </div>
    );
  }

  return (
    <div className={`cc-page ${className}`} style={style}>
      <CarnivalHeader backTo={backTo}>{headerExtra}</CarnivalHeader>
      {children}
    </div>
  );
}
